"use client";

import { useUser } from "@clerk/nextjs";
import { useEffect, useRef } from "react";

import { saveSignupAttribution } from "@/funcs/fetch/user.fetch";
import { useAuthedApi } from "@/lib/api/useAuthedApi";
import { canAttachSignupAttribution, captureSignupAttribution, parseSignupAttribution } from "@/utils/ads/signup-attribution";

// The webhook handles SignUp; this also persists OAuth transfer and resumed signups.
export default function SignupAttributionSync() {
  const { user } = useUser();
  const api = useAuthedApi();
  const submitted = useRef<string | null>(null);
  useEffect(() => {
    if (!user || submitted.current === user.id) return;
    const carried = parseSignupAttribution(user.unsafeMetadata.acquisition_attribution);
    const snapshot = carried ?? captureSignupAttribution();
    if (!snapshot || (!carried && !canAttachSignupAttribution(user.createdAt, snapshot))) return;
    submitted.current = user.id;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const send = async () => {
      attempts += 1;
      try {
        await saveSignupAttribution(snapshot, api);
      } catch {
        if (cancelled) return;
        if (attempts < 3)
          timer = setTimeout(
            () => {
              void send();
            },
            1000 * 2 ** attempts
          );
        else if (submitted.current === user.id) submitted.current = null;
      }
    };
    void send();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (submitted.current === user.id) submitted.current = null;
    };
  }, [api, user]);
  return null;
}
